/**
 * Button Square Component
 *
 * @example
 * ```html
 * <ui-button-square [icon]="SvgIcon.HOME" title="Inicio" />
 * ```
 */

import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  Input,
  Output,
  signal,
  HostBinding,
  OnInit,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { SvgIconComponent } from '../../../components/svg-icon/svg-icon.component';
import { PillSoonComponent } from '../pill-soon/pill-soon.component';

import {
  ButtonSquareState,
  ButtonSquareSize,
  ButtonSquareProps,
  ButtonSquareEvents,
  defaultButtonSquareProps,
  ComputedButtonSquareClasses,
} from './button-square.types';
import { computeButtonSquareClasses } from './button-square.styles';
import { SvgIcon } from '@services/svg-icon.service';

@Component({
  selector: 'ui-button-square',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, PillSoonComponent],
  templateUrl: './button-square.component.html',
  styleUrls: ['./button-square.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ButtonSquareComponent implements ButtonSquareProps, ButtonSquareEvents, OnInit {
  @Input() icon?: SvgIcon;
  @Input() iconUrl?: string;
  @Input({ required: true }) title!: string;
  
  private _state = signal<ButtonSquareState>(defaultButtonSquareProps.state);

  @Input()
  set state(value: ButtonSquareState) {
    this._state.set(value);
  }
  get state(): ButtonSquareState {
    return this._state();
  }
  @Input() size: ButtonSquareSize = defaultButtonSquareProps.size;
  @Input() comingSoon: boolean = defaultButtonSquareProps.comingSoon;
  @Input() propNew: boolean = defaultButtonSquareProps.propNew;
  @Input() disabled: boolean = defaultButtonSquareProps.disabled;
  @Input() controlled: boolean = false;
  @Input() className?: string;
  @Input() ariaLabel?: string;

  ngOnInit(): void {
    if (!this.icon && !this.iconUrl) {
      console.warn('ButtonSquareComponent: Either icon or iconUrl must be provided');
    }
  }

  @Output() clicked = new EventEmitter<{
    state: ButtonSquareState;
    title: string;
    icon?: SvgIcon;
    iconUrl?: string;
  }>();
  
  @Output() stateChanged = new EventEmitter<ButtonSquareState>();

  @HostBinding('class') get hostClass() {
    return this.className || '';
  }

  private readonly buttonSquareClasses = computed<ComputedButtonSquareClasses>(() =>
    computeButtonSquareClasses(this._state(), this.size, this.disabled, this.comingSoon, this.propNew, this.className)
  );

  protected readonly containerClasses = computed(() => this.buttonSquareClasses().container);
  protected readonly iconWrapperClasses = computed(() => this.buttonSquareClasses().iconWrapper);
  protected readonly iconClasses = computed(() => this.buttonSquareClasses().icon);
  protected readonly titleClasses = computed(() => this.buttonSquareClasses().title);
  protected readonly comingSoonBadgeClasses = computed(() => this.buttonSquareClasses().comingSoonBadge);
  protected readonly newBadgeClasses = computed(() => this.buttonSquareClasses().newBadge);

  protected onButtonClick(): void {
    if (this.disabled || this.comingSoon) return;

    this.clicked.emit({
      state: this.state,
      title: this.title,
      icon: this.icon,
      iconUrl: this.iconUrl,
    });

    if (!this.controlled) {
      const newState: ButtonSquareState = this._state() === 'selected' ? 'unselected' : 'selected';
      if (this._state() !== newState) {
        this._state.set(newState);
        this.stateChanged.emit(newState);
      }
    }
  }

  setState(newState: ButtonSquareState): void {
    if (this._state() !== newState) {
      this._state.set(newState);
      this.stateChanged.emit(newState);
    }
  }

  isSelected(): boolean { return this.state === 'selected'; }
  isUnselected(): boolean { return this.state === 'unselected'; }
  isInteractive(): boolean { return !this.disabled && !this.comingSoon; }
}